import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { BUSINESS } from '@/content/business'

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const describe = (now: Date): { open: boolean, text: string } => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: BUSINESS.timeZone,
        weekday: 'short',
        hour: 'numeric',
        hourCycle: 'h23'
    }).formatToParts(now)

    const weekday = parts.find(part => part.type === 'weekday')?.value ?? 'Mon'
    const hour = Number(parts.find(part => part.type === 'hour')?.value ?? '12')
    const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(weekday)
    const openToday = (BUSINESS.openDays as readonly number[]).includes(day)

    if (openToday && hour >= BUSINESS.openHour && hour < BUSINESS.closeHour) {
        return { open: true, text: "Open now until 7 PM" }
    }

    if (openToday && hour < BUSINESS.openHour) {
        return { open: false, text: "Closed now. Opens today at 7 AM" }
    }

    let next = (day + 1) % 7

    while (!(BUSINESS.openDays as readonly number[]).includes(next)) next = (next + 1) % 7

    const label = next === (day + 1) % 7 ? 'tomorrow' : DAY_NAMES[next]

    return { open: false, text: `Closed now. Opens ${label} at 7 AM` }
}

interface OpenStatusProps {
    className?: string
}

const OpenStatus: FC<OpenStatusProps> = ({ className }): ReactNode => {
    const [status, setStatus] = useState<{ open: boolean, text: string } | null>(null)

    useEffect(() => {
        const update = () => setStatus(describe(new Date()))

        update()

        const timer = window.setInterval(update, 60000)

        return () => window.clearInterval(timer)
    }, [])

    return (
        <span className={className}>
            <span
                aria-hidden='true'
                className={`mr-2 inline-block size-2.5 rounded-full align-middle ${status?.open ? 'bg-[#4ade80]' : 'bg-[#9aa7b4]'}`}
            />
            {status ? status.text : BUSINESS.hoursShort}
        </span>
    )
}

export default OpenStatus
